import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getGovernoratesOptions } from "../../../../utils/governorates";
import styles from "./main-form-create-study.module.scss";
import Dropdown from "../../../ui-kit/dropdown/dropdown";
import Input from "../../../ui-kit/Input/input";
import Button from "../../../ui-kit/button/button";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import {
  resetCreatedObject,
  resetStudyCreationState,
  setCoordinatesData,
  setGeometries,
  setPhaseTerritoryGraphic,
  setSelectedFeature,
  updateMainForm,
} from "../../../../services/reducers/master-create-study";
import {
  setEnableDrawingTools,
  stopDrawingTools,
} from "../../../../services/reducers/map";
import CoordinatesBlock from "../../../../shared/coordinates-block/coordinates-block";
import {
  convertPlotsToCoordinatesTable,
  convertPolygonToString,
} from "../../../../utils/master-plan-helper";
import { getGeometryForContour } from "../../../../services/actions/master-plan-modification";
import CustomScrollbar from "../../../ui-kit/scrollbar/scrollbar";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Polygon from "@arcgis/core/geometry/Polygon";
import {
  checkInfrastructureAvailabilityAsync,
  deleteBufferDistance,
  drawBufferDistance,
  drawVertices,
  removeTerritoryObject,
  setObjectVisibility,
  takeScreenshotAsync,
} from "../../../../services/actions/map";
import { LayersDataTypes } from "../../../../types/layers-data-types";
import ReportSuccessModal from "../report-success-modal/report-success-modal";
import LoaderPortal from "../../../../shared/loader-portal/loader-portal";
import { saveReport } from "../../../../services/api";
import { fetchReports } from "../../../../services/reducers/reports-journal";
import { debounce } from "lodash";
import FinalFormCreateStudy from "../final-form-create-study/final-form-create-study";
import { dataURLtoFile } from "../../../../utils/transformToFile";
import Tooltip from "../../../ui-kit/tooltip/tooltip";
import ConfirmTerritoryPhaseModal from "../confirm-territory-phase-modal/confirm-territory-phase-modal";
import {
  getPhaseTooltipText,
  verifyPhaseTerritory,
} from "../../../../utils/master-create-study-helper";
import Geometry from "@arcgis/core/geometry/Geometry";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { infraMap, InfrastructureTemplates } from "./conts";
import { BufferInfrastructureResults } from "../../../../interfaces/master-create-study";

export type PhaseErrorType = "notPolygon" | "sameAsSiteBoundary" | null;

const MainFormCreateStudy = () => {
  const { t, i18n } = useTranslation();

  const userName = useAppSelector((state) => state.user.user?.userName);
  const {
    options,
    mainForm,
    createdObject,
    spatialReferenceOptions,
    viewer,
    selectedFeature,
    phaseTerritory,
    bufferInfrastructureResults,
    logisticsResults,
    createdObjectInfrastructureResults,
  } = useAppSelector((state) => state.studyCreation);

  const { drawingToolsIsActive, layers } = useAppSelector((state) => state.map);

  const governoratesDisplayOptions = useMemo(
    () => getGovernoratesOptions(i18n.language),
    [i18n.language],
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [finalStep, setFinalStep] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isVisibleSuccessModal, setVisibleSuccessModal] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportId, setReportId] = useState<number | null>(null);
  const [screenshotsFiles, setScreenshotsFiles] = useState<File[]>([]);
  const [isVisibleRejectModal, setIsVisibleRejectModal] =
    useState<boolean>(false);
  const [phaseTerritoryError, setPhaseTerritoryError] =
    useState<boolean>(false);
  const [phaseErrorType, setPhaseErrorType] = useState<PhaseErrorType>(null);
  const [phaseTooltipShown, setPhaseTooltipShown] = useState<boolean>(false);

  useEffect(() => {
    if (userName) dispatch(updateMainForm({ drafter: userName }));
  }, [userName]);

  useEffect(() => {
    if (mainForm.name.length) {
      dispatch(setEnableDrawingTools(true));
    } else {
      dispatch(setEnableDrawingTools(false));
      dispatch(stopDrawingTools());
    }
  }, [mainForm.name.length]);

  useEffect(() => {
    if (drawingToolsIsActive || !verified) {
      dispatch(deleteBufferDistance());
    }
  }, [drawingToolsIsActive, verified]);

  const handleClickNext = async () => {
    if (!finalStep) {
      await prepareScreenshotsAndInfrastructure();
      setFinalStep(true);
    } else {
      handleCreateReport();
    }
  };

  const handleClickCancel = () => {
    if (finalStep) {
      setFinalStep(false);
      dispatch(drawVertices());
      dispatch(drawBufferDistance());
    } else {
      navigate("/repository/reports");
    }
  };

  const initialData = useMemo(
    () => convertPlotsToCoordinatesTable(createdObject),
    [createdObject],
  );

  const handleResetCreatedObject = () => {
    dispatch(resetCreatedObject());
  };

  const handleChangeSR = async (wkid: number, plotIdx: number) => {
    const plot = createdObject[plotIdx];
    if (!plot.newGeometry) return;

    await projection.load();

    const projectedCoordGeometry = projection.project(
      plot.newGeometry,
      new SpatialReference({ wkid }),
    ) as Polygon;

    const newStringGeom = convertPolygonToString(projectedCoordGeometry);

    dispatch(
      setGeometries({
        newGeometry: new Map([[plot.objectId, projectedCoordGeometry]]),
        stringGeometry: new Map([[plot.objectId, newStringGeom]]),
      }),
    );
  };

  const prepareScreenshotsAndInfrastructure = async () => {
    if (!viewer || !createdObject[0]?.newGeometry) return;

    setIsLoading(true);

    try {
      const elevationLayer = layers.filter((l) => l.name === "Elevation");
      const wadiNCSILayer = layers.filter((l) => l.name === "Wadi");
      const infrastructureLayers = layers.filter((l) =>
        [
          LayersDataTypes.ElectricityInfrastructure,
          LayersDataTypes.WaterInfrastructure,
          LayersDataTypes.TelecomInfrastructure,
          LayersDataTypes.SewerageInfrastructure,
          LayersDataTypes.GasInfrastructure,
        ].includes(l.dataType),
      );
      const hybridBasemapLayers = layers.filter((l) => l.name === "hybrid");
      const terrainBasemapLayers = layers.filter((l) => l.name === "terrain");
      const openStreetMapBasemapLayers = layers.filter(
        (l) => l.name === "openstreetmap",
      );

      const commonConfig = {
        geometry: createdObject[0].newGeometry,
      };

      const screenshotConfigs = [
        {
          width: 1400,
          height: 600,
          name: "1",
          layers: hybridBasemapLayers,
          zoomAdjustment: -2,
        },
        {
          width: 400,
          height: 400,
          name: "2",
          layers: [
            ...wadiNCSILayer,
            ...elevationLayer,
            ...terrainBasemapLayers,
          ],
          zoomAdjustment: -2,
        },
        {
          width: 400,
          height: 400,
          name: "3",
          layers: [
            ...wadiNCSILayer,
            ...elevationLayer,
            ...terrainBasemapLayers,
          ],
        },
        {
          width: 400,
          height: 400,
          name: "4",
          layers: [
            ...wadiNCSILayer,
            ...elevationLayer,
            ...terrainBasemapLayers,
            ...infrastructureLayers,
          ],
          zoomAdjustment: -2,
        },
        {
          width: 400,
          height: 400,
          name: "5",
          layers: [
            ...wadiNCSILayer,
            ...elevationLayer,
            ...terrainBasemapLayers,
            ...infrastructureLayers,
          ],
        },
        {
          width: 1400,
          height: 600,
          name: "6",
          layers: [...openStreetMapBasemapLayers, ...infrastructureLayers],
          zoomAdjustment: -1,
        },
        {
          width: 1400,
          height: 600,
          name: "7",
          layers: [...terrainBasemapLayers, ...elevationLayer],
          zoomAdjustment: -1,
        },
        {
          width: 1600,
          height: 900,
          name: "8",
          layers: [...hybridBasemapLayers],
        },
      ].map((cfg) => ({
        ...commonConfig,
        ...cfg,
      }));

      await checkInfrastructureAvailabilityAsync(dispatch);
      const results = await dispatch(takeScreenshotAsync(screenshotConfigs));

      const files = await Promise.all(
        results.map(({ url, name }) => dataURLtoFile(url, `${name}.png`)),
      );

      setScreenshotsFiles(files.filter((file): file is File => !!file));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateReport = async () => {
    setIsLoading(true);

    try {
      if (drawingToolsIsActive) {
        dispatch(stopDrawingTools());
      }

      const formData = new FormData();

      formData.append("CityName", mainForm.name);
      formData.append("Governorate", mainForm.governorate);
      formData.append("Status", mainForm.status.toString());
      formData.append("Area", createdObject[0].area!);
      formData.append("Geometry", JSON.stringify(createdObject[0].newGeometry));
      screenshotsFiles
        .filter((file): file is File => !!file)
        .forEach((file) => {
          formData.append("Images", file, file.name);
        });

      const firstPhaseGeometry = phaseTerritory.graphic?.geometry;

      if (firstPhaseGeometry) {
        formData.append(
          "firstPhaseGeometry",
          JSON.stringify(firstPhaseGeometry),
        );
        formData.append("firstPhaseArea", phaseTerritory.area.toString());
      }

      for (const [label, field] of Object.entries(infraMap)) {
        const value =
          bufferInfrastructureResults[
            field as keyof BufferInfrastructureResults
          ];

        formData.append(
          `Attributes.Sa${label}`,
          value.available ? value.distance.toString() : "",
        );
        formData.append(
          `Attributes.Ar${label}`,
          value.available ? "Available" : "Empty",
        );
      }
      formData.append("Attributes.ArWadi", "Empty");
      formData.append("Attributes.ArLeverage", "Empty");
      formData.append("Attributes.MaxElevation", "");
      formData.append("Attributes.MinElevation", "");
      formData.append("Attributes.DistToWilayatCenter", "");
      formData.append(
        "Attributes.MuscatAirportDist",
        logisticsResults.airportDistance?.toString() ?? "",
      );
      formData.append(
        "Attributes.FirstPortName",
        logisticsResults.FirstPortName || "",
      );
      formData.append(
        "Attributes.FirstPortDist",
        logisticsResults.FirstPortDist?.toString() || "0",
      );
      formData.append(
        "Attributes.SecondPortName",
        logisticsResults.SecondPortName || "",
      );
      formData.append(
        "Attributes.SecondPortDist",
        logisticsResults.SecondPortDist?.toString() || "0",
      );

      Object.entries(createdObjectInfrastructureResults).forEach(
        ([infrastructureId, { distance, direction }]) => {
          const infrastructureTypeId = Number(infrastructureId);
          const template =
            InfrastructureTemplates[
              infrastructureTypeId as keyof typeof InfrastructureTemplates
            ];

          if (!template) return;

          const attributeKey = `Attributes.${template.key}`;

          if (distance === null) {
            formData.append(
              attributeKey,
              "The closest point of the considered network is located within the city limits.",
            );
          } else if (direction !== null) {
            const populatedTemplate = template.template
              .replace("{distance}", distance.toString())
              .replace("{direction}", direction);
            formData.append(attributeKey, populatedTemplate);
          }
        },
      );

      const reportId = await saveReport(formData);
      setReportId(reportId);
      setVisibleSuccessModal(true);
      dispatch(fetchReports());
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setVisibleSuccessModal(false);
    navigate("/repository/reports");
    dispatch(resetStudyCreationState());
  };

  const handleBufferChange = (value: string) => {
    const regExp = /^\d*(\.\d{0,2})?$/;
    if (value === "" || regExp.test(value)) {
      dispatch(updateMainForm({ buffer: value }));
      debouncedApplyBufferChange();
    }
  };

  const debouncedApplyBufferChange = useMemo(
    () =>
      debounce(() => {
        dispatch(drawBufferDistance());
      }, 1000),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      debouncedApplyBufferChange.cancel();
    };
  }, [debouncedApplyBufferChange]);

  const handleConfirmPhaseTerritory = async () => {
    setPhaseTooltipShown(false);
    setPhaseErrorType(null);

    if (selectedFeature?.geometry.type !== "polygon") {
      setPhaseErrorType("notPolygon");
      setPhaseTerritoryError(true);
      return;
    }

    const coordObjectGeometry = createdObject[0].newGeometry as Geometry;
    const selectedObjectGeometry = selectedFeature.geometry;

    if (geometryEngine.equals(coordObjectGeometry, selectedObjectGeometry)) {
      setPhaseErrorType("sameAsSiteBoundary");
      setPhaseTerritoryError(true);
      return;
    }

    if (phaseTerritoryError) {
      setPhaseTerritoryError(false);
    }

    if (phaseTerritoryError) setPhaseTerritoryError(false);

    const { isContains, projectedInnerGeometry } = await verifyPhaseTerritory(
      createdObject[0].newGeometry as Geometry,
      selectedFeature,
    );

    if (isContains) {
      dispatch(setPhaseTerritoryGraphic(projectedInnerGeometry));
      dispatch(stopDrawingTools());
      dispatch(drawBufferDistance());
    } else {
      setIsVisibleRejectModal(true);
    }
  };

  const handleOneTimeTooltipShow = () => {
    if (phaseTerritoryError && !phaseTooltipShown) {
      setPhaseTooltipShown(true);
    }
  };

  const handleVisiblePhaseTerritory = () => {
    const isHidden = !phaseTerritory.visible;
    const uuid = phaseTerritory.graphic?.attributes?.uuid;
    if (uuid) {
      dispatch(setObjectVisibility(uuid, isHidden));
    }
  };

  const handleDeletePhaseTerritory = () => {
    const phaseTerritoryObjectId = phaseTerritory.graphic?.attributes?.uuid;
    const selectedObjectId = selectedFeature?.attributes?.uuid;

    if (phaseTerritoryObjectId) {
      dispatch(removeTerritoryObject({ objectId: phaseTerritoryObjectId }));
    }

    dispatch(setPhaseTerritoryGraphic(null));

    if (phaseTerritoryObjectId === selectedObjectId) {
      dispatch(setSelectedFeature(null));
    }
  };

  useEffect(() => {
    if (!verified && phaseTerritory.graphic) {
      handleDeletePhaseTerritory();
    }
  }, [verified, phaseTerritory.graphic]);

  return (
    <div className={styles.mainForm}>
      <CustomScrollbar>
        <div className={styles.scrollContent}>
          {finalStep ? (
            <FinalFormCreateStudy />
          ) : (
            <>
              <div className={styles.draftStatusGroup}>
                <Input
                  classes={{
                    label: styles.label,
                    input: styles.formField,
                  }}
                  label={t("main-form-create-study.drafterLabel")}
                  value={mainForm.drafter}
                  disabled={true}
                />
                <Dropdown
                  classes={{
                    label: styles.label,
                    dropdown: styles.formField,
                  }}
                  title={t("main-form-create-study.statusLabel")}
                  options={options.statusOptions}
                  value={mainForm.status}
                  disabled={true}
                />
              </div>
              <Input
                classes={{
                  input: styles.formField,
                  label: styles.label,
                }}
                label={t("main-form-create-study.cityNameLabel")}
                value={mainForm.name}
                onChange={(value) => dispatch(updateMainForm({ name: value }))}
                disabled={finalStep}
                tooltip={{
                  text: t("main-form-create-study.cityNameTooltip"),
                }}
              />
              <div className={styles.coordBlock}>
                <div className={styles.coordHeader}>
                  <span className={styles.label}>
                    {t("main-form-create-study.coordinateToolsLabel")}
                  </span>
                  <Tooltip
                    text={
                      <ul className={styles.tooltipList}>
                        <li>
                          {t("main-form-create-study.coordinateToolsTooltip1")}
                        </li>
                        <li>
                          {t("main-form-create-study.coordinateToolsTooltip2")}
                        </li>
                      </ul>
                    }
                  />
                </div>
                <CoordinatesBlock
                  verified={verified}
                  setVerified={setVerified}
                  initialData={initialData}
                  onSetGeometries={({ newGeometry, stringGeometry }) =>
                    dispatch(setGeometries({ newGeometry, stringGeometry }))
                  }
                  onSetCoordinatesData={(data) =>
                    dispatch(setCoordinatesData(data))
                  }
                  onGetGeometry={(plotIdx, contourIdx) =>
                    dispatch(getGeometryForContour({ plotIdx, contourIdx }))
                  }
                  titleTable={t("main-form-create-study.coordinatesTitle")}
                  validationOptions={{
                    data: {
                      current: createdObject,
                    },
                    flags: {
                      selfIntersections: true,
                    },
                    ui: {
                      modalMessage: verified
                        ? t("main-form-create-study.modalMessagePositive")
                        : t("main-form-create-study.modalMessageNegative"),
                      modalTitle: t("main-form-create-study.modalTitle"),
                      onResetChanges: handleResetCreatedObject,
                    },
                  }}
                  disabled={!mainForm.name.length}
                  changeSpatialReferenceConfig={{
                    options: spatialReferenceOptions,
                    onChangeSR: handleChangeSR,
                  }}
                  disabledStopDrawingToolsAfterVerify={false}
                  disableResetOnDrawingTools={false}
                />
              </div>
              <div
                className={`${styles.territoryDialog} ${!verified && styles.disabled}`}
              >
                {!phaseTerritory.graphic ? (
                  <div className={styles.territoryWrapper}>
                    <span className={styles.territoryText}>
                      {t("main-form-create-study.drawTerritoryPrompt")}
                    </span>
                    <div className={styles.btnGroup}>
                      <Tooltip
                        delay={0}
                        position="bottom"
                        text={getPhaseTooltipText(phaseErrorType, t)}
                        disabled={!phaseTerritoryError || phaseTooltipShown}
                      >
                        <button
                          className={styles.confirmBtn}
                          onClick={handleConfirmPhaseTerritory}
                          onMouseEnter={() => handleOneTimeTooltipShow()}
                        >
                          <div className={styles.confirmIcon} />
                          {t("main-form-create-study.confirmTerritory")}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <div className={styles.territoryWrapper}>
                    <span className={`${styles.territoryText} ${styles.bold}`}>
                      {t("main-form-create-study.territoryPhaseLabel")}
                    </span>
                    <div className={styles.btnGroup}>
                      <button
                        className={styles.confirmBtn}
                        onClick={handleVisiblePhaseTerritory}
                      >
                        <div
                          className={
                            !phaseTerritory.visible
                              ? styles.showIcon
                              : styles.hideIcon
                          }
                        />
                        {!phaseTerritory.visible
                          ? t("main-form-create-study.hideOnMap")
                          : t("main-form-create-study.showOnMap")}
                      </button>
                      <button
                        className={styles.cancelBtn}
                        onClick={handleDeletePhaseTerritory}
                      >
                        <div className={styles.deleteIcon} />
                        {t("main-form-create-study.deleteTerritory")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.draftStatusGroup}>
                <Input
                  classes={{
                    input: styles.formField,
                    label: styles.label,
                    container: styles.input,
                  }}
                  disabled={!verified || !phaseTerritory.graphic}
                  label={t("main-form-create-study.bufferLabel")}
                  value={mainForm.buffer}
                  onChange={handleBufferChange}
                  tooltip={{
                    text: (
                      <ul className={styles.tooltipList}>
                        <li>{t("main-form-create-study.bufferTooltip1")}</li>
                        <li>{t("main-form-create-study.bufferTooltip2")}</li>
                      </ul>
                    ),
                  }}
                />
                <Dropdown
                  classes={{
                    label: styles.label,
                    dropdown: styles.formField,
                  }}
                  title={t("main-form-create-study.governoratesLabel")}
                  options={governoratesDisplayOptions}
                  disabled={!verified || !phaseTerritory.graphic}
                  value={mainForm.governorate}
                  onClear={() => dispatch(updateMainForm({ governorate: "" }))}
                  onChange={(value) =>
                    dispatch(
                      updateMainForm({
                        governorate:
                          typeof value === "string" ? value : undefined,
                      }),
                    )
                  }
                  directionMenu="up"
                />
              </div>
            </>
          )}
        </div>
      </CustomScrollbar>
      <div className={styles.bottomBtns}>
        <Button
          type="primary"
          className={styles.btn}
          onClick={handleClickNext}
          disabled={
            !mainForm.name.trim() ||
            !mainForm.governorate.trim() ||
            !mainForm.buffer.trim() ||
            (finalStep && !verified)
          }
        >
          {finalStep
            ? t("main-form-create-study.createReportButton")
            : t("main-form-create-study.nextButton")}
        </Button>
        <Button
          type="outline"
          className={styles.btn}
          onClick={handleClickCancel}
        >
          {finalStep
            ? t("main-form-create-study.returnButton")
            : t("main-form-create-study.cancelButton")}
        </Button>
      </div>
      {isVisibleSuccessModal && reportId && (
        <ReportSuccessModal
          reportId={reportId}
          cityName={mainForm.name}
          onClose={handleCloseSuccessModal}
        />
      )}
      {isLoading && <LoaderPortal />}
      {isVisibleRejectModal && (
        <ConfirmTerritoryPhaseModal
          onClose={() => setIsVisibleRejectModal(false)}
        />
      )}
    </div>
  );
};

export default MainFormCreateStudy;
